"use client";

import React from "react";
import { ArrowRight, ChartBar as BarChart3, Bot, CircleCheck as CheckCircle2, Code as Code2, Compass, FileSearch, Globe as Globe2, Layers, Link2, MapPin, RefreshCw, Search, ShoppingCart, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  icon: Compass,
  badge: "Foundational",
  title: "SEO strategy & organic roadmap",
  description: "When you hire me, I do not hand you a generic slide deck. I analyse your commercial goals, assess your competitive landscape, and build a bespoke organic search roadmap that targets real buyers across your target markets, whether regional, national, or international.",
  linkText: "Explore my freelance SEO approach",
  linkTo: "/FreelanceSeoConsultantPage",
  contextSnippet: "A direct alternative to bloated agency retainers."
}, {
  icon: Code2,
  badge: "Core Technical",
  title: "Technical SEO & website health",
  description: "Search engines cannot reward pages they struggle to crawl, render, or index. I resolve site architecture bottlenecks, Core Web Vitals deficits, indexation conflicts, and complex JavaScript rendering issues to ensure clean search visibility.",
  linkText: "View technical SEO consulting",
  linkTo: "/TechnicalSeoPage",
  contextSnippet: "Direct collaboration with your developers."
}, {
  icon: Search,
  badge: "Market Research",
  title: "Intent-led keyword research",
  description: "I map genuine search demand across every stage of your customer journey. Rather than chasing vanity search volumes, I identify high-intent queries that drive qualified inquiries and revenue for your business across target territories.",
  linkText: "See national SEO solutions",
  linkTo: "/NationalSeoPage",
  contextSnippet: "Target commercial search intent."
}, {
  icon: FileSearch,
  badge: "In-Depth Diagnosis",
  title: "Comprehensive SEO audits",
  description: "My in-depth audits uncover what holds your domain back. I evaluate technical hygiene, content depth, internal linking, and competitor positioning, giving you a prioritised action plan based on commercial impact.",
  linkText: "Request an SEO audit",
  linkTo: "/SeoAuditsPage",
  contextSnippet: "Clear, prioritised fixes without jargon."
}, {
  icon: Layers,
  badge: "On-Page & Architecture",
  title: "On-page SEO & content structure",
  description: "I optimise your page hierarchy, semantic topical coverage, title tags, and meta structures. I ensure every page clearly answers search intent and earns authority across search algorithms.",
  linkText: "Learn about SEO agency alternative",
  linkTo: "/SEOAgencyAlternativeUkPage",
  contextSnippet: "Precise semantic optimisation."
}, {
  icon: MapPin,
  badge: "Local Visibility",
  title: "Local SEO & regional presence",
  description: "Whether targeting customers in a single city or across multiple regional territories worldwide, I structure your Google Business Profile, local citations, and geo-targeted landing pages to improve visibility when local prospects search.",
  linkText: "Explore local SEO services",
  linkTo: "/LocalSEOPage",
  contextSnippet: "Improve visibility for high-value local searches."
}, {
  icon: BarChart3,
  badge: "High-Ticket B2B",
  title: "B2B SEO & lead generation",
  description: "B2B search journeys are complex with long decision cycles. I align your organic presence with executive search intent, case studies, and solution pages that generate qualified pipeline leads.",
  linkText: "View B2B SEO consultancy",
  linkTo: "/B2BSeoLeadGenerationPage",
  contextSnippet: "Designed for qualified B2B pipeline."
}, {
  icon: ShoppingCart,
  badge: "Revenue Focused",
  title: "Ecommerce SEO & revenue growth",
  description: "For online stores, search traffic must convert into sales. I streamline faceted navigation, category hierarchies, product schema, and commercial collection pages to capture ready-to-buy shoppers.",
  linkText: "Review ecommerce SEO services",
  linkTo: "/EcommerceSeoPage",
  contextSnippet: "Category optimisation and schema markup."
}, {
  icon: Bot,
  badge: "Future-Proof",
  title: "AI search & generative optimisation",
  description: "Search is evolving with AI Overviews and conversational assistants. I help your brand improve the signals and content structure that can support visibility and citations in AI-powered search, alongside clear entity associations and concise topical answers that generative systems reference.",
  linkText: "Discover AI search optimisation",
  linkTo: "/AISearchOptimisationPage",
  contextSnippet: "Optimised for Google AI Overviews."
}, {
  icon: Globe2,
  badge: "Multi-Market",
  title: "International SEO & global expansion",
  description: "Expanding across multiple countries requires precise hreflang implementation, international site architecture, and localized search targeting to protect domain equity in each territory.",
  linkText: "Read about international SEO",
  linkTo: "/InternationalSeoPage",
  contextSnippet: "Multi-language and geo-targeting setups."
}, {
  icon: Link2,
  badge: "Authority",
  title: "Natural link acquisition & digital PR",
  description: "I do not buy spammy backlinks or use risky private networks. I secure legitimate global and regional editorial opportunities, unlinked brand mentions, and link-worthy resource gaps that build durable domain authority.",
  linkText: "See affordable SEO services in the UK",
  linkTo: "/AffordableSeoServicesUkPage",
  contextSnippet: "Ethical, white-hat authority building."
}, {
  icon: RefreshCw,
  badge: "Ongoing Partnership",
  title: "Monthly advisory & retained support",
  description: "Organic growth requires consistent monitoring, technical QA, and tactical iterations. As your dedicated consultant, I provide continuous guidance, transparent reporting, and monthly execution reviews across any time zone.",
  linkText: "Check monthly SEO packages",
  linkTo: "/MonthlySeoServicesPage",
  contextSnippet: "Retained consulting tailored to your scope."
}];
export default function Consulting() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultingservicesgrid" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium px-3 py-1 text-xs uppercase tracking-wide">
              Consulting capabilities
            </Badge>
            <span className="text-xs text-muted-foreground font-mono">
              Evaluating consulting scopes
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default mb-6 leading-tight">
            Comprehensive SEO services covering strategy, technical execution, and growth, worldwide.
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Explore the core consulting specialisms below to see how each targeted SEO discipline drives sustainable organic search performance.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={i} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.45,
            delay: i % 3 * 0.1
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card/60 hover:bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,background-color,box-shadow] duration-200">
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs font-normal border-border/80 text-muted-foreground bg-background/50">
                        {service.badge}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl font-semibold text-foreground font-default tracking-tight mb-2">
                      <h3 className="text-xl font-semibold">{service.title}</h3>
                    </CardTitle>

                    <p className="text-xs text-primary font-medium">
                      {service.contextSnippet}
                    </p>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="pt-4 border-t border-border/60">
                      <Link to={service.linkTo} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors group">
                        <span>{service.linkText}</span>
                        <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Mid-page Direct Contact / Value Banner */}
        <div className="mt-16 rounded-2xl bg-secondary/50 border border-border p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-2 text-primary font-medium text-sm">
              <Sparkles className="size-4" />
              <span>Independent SEO Consultancy, Worldwide</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight font-default mb-3">
              Not sure which SEO service your business needs first?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              I review your current search visibility, identify high-priority
              technical hurdles, and recommend the exact strategic steps to grow
              organic traffic.
            </p>
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                Founder-level insight.
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                No sales pressure or lock-in
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                Clear commercial scope
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto shadow-sm">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/SeoAuditsPage">Get a Free SEO Audit</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}